import { registerDestructor } from '@ember/destroyable';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import Modifier, { type ArgsFor } from 'ember-modifier';

import type { Service } from 'webapp-types';

import type { MetricInfoCardOpenOptions, MetricInfoModel } from 'webapp/services/metric-info';

type MetricInfoTrigger = 'hover' | 'click';

// A surface's own flow in place of the host's, for its anchor's opens (the designer's and the library's Connect).
type MetricInfoSurfaceFlows = NonNullable<MetricInfoCardOpenOptions['host']>;

interface MetricInfoAnchorSignature {
	Element: HTMLElement;
	Args: {
		Positional: [MetricInfoModel];
		Named: Omit<MetricInfoCardOpenOptions, 'host'> &
			MetricInfoSurfaceFlows & { trigger: MetricInfoTrigger };
	};
}

const isActivationKey = (event: KeyboardEvent): boolean =>
	event.key === 'Enter' || event.key === ' ';

/**
 * Makes the element the metric info card's anchor (`services/metric-info`).
 * - `trigger='hover'`: a mouse hover opens the card, leaving closes it; a click, a tap, Enter or Space pins it.
 * - `trigger='click'`: a click, Enter or Space opens and pins it, a second one closes it.
 * The click never reaches the anchor's parents (a field that opens the Metric Select, a library tile).
 */
export default class MetricInfoAnchorModifier extends Modifier<MetricInfoAnchorSignature> {
	@service('metric-info') declare metricInfoService: Service.MetricInfo;

	private element: HTMLElement | null = null;
	private model: MetricInfoModel | null = null;
	private options: MetricInfoCardOpenOptions | null = null;

	constructor(owner: Owner, args: ArgsFor<MetricInfoAnchorSignature>) {
		super(owner, args);

		registerDestructor(this, () => this.detach());
	}

	override modify(
		element: HTMLElement,
		[model]: [MetricInfoModel],
		{
			trigger,
			mode,
			hideActions,
			theme,
			onConnect,
		}: MetricInfoAnchorSignature['Args']['Named'],
	): void {
		this.model = model;
		this.options = { mode, hideActions, theme, host: onConnect ? { onConnect } : undefined };

		if (this.element === element) {
			return;
		}

		this.detach();
		this.element = element;

		// A call site's trigger never changes, so it is read once, when the element is set up.
		if (trigger === 'hover') {
			element.addEventListener('pointerenter', this.onPointerEnter);
			element.addEventListener('pointerleave', this.onPointerLeave);
		}

		element.addEventListener('click', this.onClick);
		element.addEventListener('keydown', this.onKeyDown);
	}

	private detach(): void {
		const { element } = this;

		if (!element) {
			return;
		}

		element.removeEventListener('pointerenter', this.onPointerEnter);
		element.removeEventListener('pointerleave', this.onPointerLeave);
		element.removeEventListener('click', this.onClick);
		element.removeEventListener('keydown', this.onKeyDown);
		this.metricInfoService.closeIfAnchor(element);
		this.element = null;
	}

	private toggle(): void {
		if (this.element && this.model && this.options) {
			this.metricInfoService.toggle(this.element, this.model, this.options);
		}
	}

	// A touch fires these too; its tap is the click below.
	private onPointerEnter = (event: PointerEvent): void => {
		if (event.pointerType === 'touch') {
			return;
		}

		if (this.element && this.model && this.options) {
			this.metricInfoService.hoverEnter(this.element, this.model, this.options);
		}
	};

	private onPointerLeave = (event: PointerEvent): void => {
		if (event.pointerType !== 'touch') {
			this.metricInfoService.hoverLeave();
		}
	};

	private onClick = (event: MouseEvent): void => {
		event.preventDefault();
		event.stopPropagation();
		this.toggle();
	};

	// A <button> clicks on Enter and Space by itself.
	private onKeyDown = (event: KeyboardEvent): void => {
		if (!isActivationKey(event) || this.element instanceof HTMLButtonElement) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		this.toggle();
	};
}
